/**
 * AdminAuditTrustPage — self-updating "Trust Dashboard"
 *
 * Fetches `/api/admin/audit-trust` and renders the latest synthetic clinical
 * validation results. Public credibility surface — no admin gate (so investors
 * and clinical validators can revisit a single URL any time).
 *
 * May 30, 2026 (Sub-batch D). NO LLM. Additive.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


export default function AdminAuditTrustPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/audit-trust`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.message || 'fetch error'));
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 font-mono text-sm">
        <div className="max-w-3xl mx-auto p-6 bg-white border border-red-300 rounded">
          <h2 className="text-lg font-bold text-red-700 mb-2">Audit results unavailable</h2>
          <p className="text-slate-700">{err}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 font-mono text-sm text-slate-500">
        Loading audit results…
      </div>
    );
  }

  if (data.available === false) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 font-mono text-sm">
        <div className="max-w-3xl mx-auto p-6 bg-white border border-amber-300 rounded">
          <h2 className="text-lg font-bold text-amber-700 mb-2">No audit results yet</h2>
          <p className="text-slate-700">{data.message || 'Run the synthetic validation suite to populate this page.'}</p>
        </div>
      </div>
    );
  }

  const passColor = data.pass_rate_pct >= 95 ? 'text-emerald-700' : data.pass_rate_pct >= 80 ? 'text-amber-700' : 'text-red-700';
  const passBg = data.pass_rate_pct >= 95 ? 'bg-emerald-50 border-emerald-300' : data.pass_rate_pct >= 80 ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300';

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-10" data-testid="audit-trust-page">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="bg-slate-900 text-white p-6 rounded-t border-b-4 border-emerald-500">
          <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold mb-1">P.Ai Global Health · Synthetic Clinical Validation</div>
          <h1 className="text-2xl font-bold">Trust Dashboard — Live Determinism Audit</h1>
          <p className="text-sm text-slate-300 mt-2">
            Last run: <strong className="text-emerald-300">{data.generated_at || 'unknown'}</strong> · Sub-batch: <strong>{data.subbatch || 'unknown'}</strong>
          </p>
          <p className="text-xs text-slate-400 mt-1 italic">
            Every clinical flag is generated by a deterministic Python rule. Zero LLMs in the clinical decision loop. Numbers below update automatically every time the synthetic suite is re-run.
          </p>
        </div>

        {/* Top tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <div className="bg-white border border-slate-200 rounded p-4">
            <div className="text-xs uppercase text-slate-500 font-semibold tracking-wider">Engines Live</div>
            <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="trust-engines-count">{data.summary?.engines_live ?? '—'}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded p-4">
            <div className="text-xs uppercase text-slate-500 font-semibold tracking-wider">Pytest PASS</div>
            <div className="text-3xl font-bold text-emerald-700 mt-1" data-testid="trust-pytest-count">
              {data.summary?.pytest_pass ?? '—'}<span className="text-base text-slate-500">/{data.summary?.pytest_total ?? '—'}</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded p-4">
            <div className="text-xs uppercase text-slate-500 font-semibold tracking-wider">LLMs in Clinical Loop</div>
            <div className="text-3xl font-bold text-emerald-700 mt-1" data-testid="trust-llm-zero">0</div>
          </div>
          <div className={`border-2 rounded p-4 ${passBg}`}>
            <div className="text-xs uppercase text-slate-500 font-semibold tracking-wider">New Scenarios PASS</div>
            <div className={`text-3xl font-bold mt-1 ${passColor}`} data-testid="trust-scenario-pct">
              {data.pass_rate_pct ?? '—'}<span className="text-base text-slate-500">%</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">{data.pass_count}/{data.scenario_count} scenarios</div>
          </div>
        </div>

        {/* Scenario detail table */}
        <div className="mt-8 bg-white border border-slate-200 rounded overflow-hidden">
          <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Scenario-by-scenario results</h2>
          </div>
          <table className="w-full text-xs font-mono" data-testid="trust-scenario-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 border-b border-slate-200">#</th>
                <th className="text-left px-3 py-2 border-b border-slate-200">Engine</th>
                <th className="text-left px-3 py-2 border-b border-slate-200">Expected category</th>
                <th className="text-left px-3 py-2 border-b border-slate-200">Severity</th>
                <th className="text-left px-3 py-2 border-b border-slate-200">Status</th>
                <th className="text-left px-3 py-2 border-b border-slate-200">Actual</th>
              </tr>
            </thead>
            <tbody>
              {(data.scenarios || []).map((s, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                  <td className="px-3 py-2 text-slate-800">{s.engine}</td>
                  <td className="px-3 py-2 text-slate-700">{s.expected_cat}</td>
                  <td className="px-3 py-2 text-slate-700">{s.expected_sev}</td>
                  <td className="px-3 py-2">
                    {s.status === 'PASS'
                      ? <span className="text-emerald-700 font-semibold">✓ PASS</span>
                      : <span className="text-red-700 font-semibold">✗ FAIL</span>
                    }
                  </td>
                  <td className="px-3 py-2 text-slate-600 truncate max-w-[280px]">{s.actual || s.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer narrative */}
        <div className="mt-8 p-5 bg-slate-100 border-l-4 border-slate-700 rounded text-sm text-slate-700 leading-relaxed">
          <p className="mb-2"><strong className="text-slate-900">What you are seeing.</strong> This is the engineering-side validation that every clinical safety pattern fires correctly through the full P.Ai pipeline. The scenarios above represent classic textbook clinical presentations — snakebite, eclampsia, neonatal sepsis, testicular torsion, ACS, sepsis, cauda equina, etc. — each tested deterministically end-to-end.</p>
          <p className="mb-2"><strong className="text-slate-900">What this is NOT.</strong> This page does not replace the human clinical attestation by Dr. Archna · Sajan · Sharon · Sujatha · Seetal (medico-legal layer, separate workstream).</p>
          <p className="text-xs italic text-slate-500 mt-3">Re-run this audit any time: <code>cd /app/backend &amp;&amp; PYTHONPATH=/app/backend python3 tests/synthetic_subbatch_d.py</code></p>
        </div>

      </div>
    </div>
  );
}
